/**
 * Data-driven settings rows and the pure adjust logic behind the settings panel (unit tested).
 */
import type { Settings } from '@/core/types';

export type RowKind = 'slider' | 'toggle' | 'cycle';
export interface CycleOption { value: Settings[keyof Settings]; label: string }
export interface SettingsRow { key: keyof Settings; label: string; kind: RowKind; options?: CycleOption[] }

export const SETTINGS_ROWS: SettingsRow[] = [
  { key: 'musicVolume', label: '音乐音量', kind: 'slider' },
  { key: 'ambienceVolume', label: '环境音量', kind: 'slider' },
  { key: 'sfxVolume', label: '音效音量', kind: 'slider' },
  { key: 'muted', label: '静音', kind: 'toggle' },
  { key: 'textSpeed', label: '文字速度', kind: 'cycle', options: [
    { value: 'slow', label: '慢' }, { value: 'normal', label: '中' }, { value: 'fast', label: '快' }, { value: 'instant', label: '即显' },
  ] },
  { key: 'reduceMotion', label: '减少动态', kind: 'toggle' },
  { key: 'fullscreen', label: '全屏', kind: 'toggle' },
  { key: 'zoom', label: '画面缩放', kind: 'cycle', options: [
    { value: 'auto', label: '自动' }, { value: 1, label: '1×' }, { value: 2, label: '2×' }, { value: 3, label: '3×' },
  ] },
];

export const SLIDER_STEP = 0.1;

/**
 * Returns the patch produced by nudging a row. `dir` -1/+1 for left/right; 0 for "activate"
 * (toggle flips, cycle advances, slider does nothing).
 */
export function adjustSetting(settings: Settings, row: SettingsRow, dir: -1 | 0 | 1): Partial<Settings> {
  const cur = settings[row.key];
  switch (row.kind) {
    case 'slider': {
      if (dir === 0) return {};
      const v = Math.round(Math.min(1, Math.max(0, (cur as number) + dir * SLIDER_STEP)) * 100) / 100;
      return { [row.key]: v } as Partial<Settings>;
    }
    case 'toggle':
      return { [row.key]: !(cur as boolean) } as Partial<Settings>;
    case 'cycle': {
      const opts = row.options ?? [];
      if (opts.length === 0) return {};
      const idx = Math.max(0, opts.findIndex((o) => o.value === cur));
      const step = dir === 0 ? 1 : dir;
      const next = opts[(idx + step + opts.length) % opts.length];
      return next ? ({ [row.key]: next.value } as Partial<Settings>) : {};
    }
  }
}

/** Display string for a row's current value. */
export function formatSettingValue(settings: Settings, row: SettingsRow): string {
  const cur = settings[row.key];
  switch (row.kind) {
    case 'slider': return `${Math.round((cur as number) * 100)}%`;
    case 'toggle': return (cur as boolean) ? '开' : '关';
    case 'cycle': return row.options?.find((o) => o.value === cur)?.label ?? String(cur);
  }
}
